import type { ComponentProps } from 'react';

import type { TimelineFeedSectionInput } from './loggerContext';
import { NoteComposer } from './NoteComposer';
import { TimelineList } from './TimelineList';

// The bottom of the timeline logger: the note composer and the
// reverse-chronological feed. Split out of TimelineLoggerScreen
// when LG-29's rest-freeze wraps pushed the screen over the 500-line cap (CN-27
// had already flagged it at 496 — "the next touch splits first, not during").
// Pure render shell: it reads the same context bag the screen does (mirroring
// useTimelineLogger(ctx)) plus the feed/handlers the screen derives, and owns no
// state of its own.
//
export function TimelineFeedSection({
  ctx,
  feed,
  onSendNote,
  onDeleteNote,
  onFocusRow,
  onUncompleteSet,
  bestE1rmForSlug,
  failFlash,
  landedFocus,
}: {
  ctx: TimelineFeedSectionInput;
  feed: ComponentProps<typeof TimelineList>['feed'];
  onSendNote: ComponentProps<typeof NoteComposer>['onSend'];
  onDeleteNote: ComponentProps<typeof TimelineList>['onDeleteNote'];
  onFocusRow: ComponentProps<typeof TimelineList>['onFocusRow'];
  onUncompleteSet: ComponentProps<typeof TimelineList>['onUncompleteSet'];
  bestE1rmForSlug: ComponentProps<typeof TimelineList>['bestE1rmForSlug'];
  failFlash: ComponentProps<typeof TimelineList>['flashRow'];
  landedFocus: ComponentProps<typeof TimelineList>['landedFocus'];
}) {
  const {
    draft,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    groupLabelFor,
  } = ctx;
  return (
    <>
      {/* Notes + feed live OUTSIDE the exercise gate — a note isn't tied to
          an exercise, so the composer is visible from the very START and
          detached from the focus card (Antenore 2026-06-04, the design
          intent: "appena prima della timeline"). Both render whether or not
          any exercise has been added. Composer stays LIVE during a rest. */}
      <NoteComposer onSend={onSendNote} t={t} theme={theme} />

      {/* Add-exercise used to sit here, between the note composer and the feed
          (inbox bdfb84a3). It moved to the roster strip's fixed slot (PE-22
          fetta E): down here it was below the fold as soon as a session had a
          few sets in it, and it was one of four hand-rolled copies. */}

      {/* Chronological feed: logged sets + note mini-cards, interleaved by
          time (oldest first). A mini-card tap re-focuses that set's exercise,
          during a rest like at any other time (TM-18b). */}
      <TimelineList
        feed={feed}
        startedAt={draft.startedAt}
        resolveName={getExerciseNameBySlug}
        groupLabel={(groupId, kind) =>
          groupId
            ? groupLabelFor({ id: groupId, kind: kind ?? 'superset' })
            : null
        }
        onFocusRow={onFocusRow}
        onUncompleteSet={onUncompleteSet}
        onDeleteNote={onDeleteNote}
        bestE1rmForSlug={bestE1rmForSlug}
        flashRow={failFlash}
        landedFocus={landedFocus}
        locale={i18n.language}
        theme={theme}
      />
    </>
  );
}
